import { Group, Button as MButton } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPlus } from '@tabler/icons-react';
import type {
  CreatePaymentPlanBody,
  Id,
  PaymentPlanComplete,
  PaymentStepsAttributes,
} from 'api/models';
import classNames from 'classnames/bind';
import { Panel } from 'components/Panel';
import dayjs from 'dayjs';
import { i18nKeys, useTranslation } from 'locales';
import { get } from 'lodash-es';
import { useEffect } from 'react';
import { FormProvider, type SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { isDefined } from 'remeda';
import Amount from 'shared/components/Amount';
import { Icon, IconName } from 'shared/components/Icon';
import { Button, ButtonColor, DateSelector, Input } from 'shared/io';
import { currencySymbol } from 'shared/utils/currency';

import { adjustNextAmounts, adjustNextDatesSnakeCased } from '../PaymentPlan.utils';
import styleIdentifiers from '../PaymentPlanForm/PaymentPlanForm.module.scss';
import { PaymentPlanFormWrapper } from '../PaymentPlanForm/PaymentPlanFormWrapper';
import { RecalculateDatesModal } from '../PaymentPlanWizardComponents';
import { PaymentPlanProperties } from './PaymentPlanProperties';

const styles = classNames.bind(styleIdentifiers);

export type PartialUpdatePaymentPlanBody = Pick<
  CreatePaymentPlanBody,
  | 'payment_steps_attributes'
  | 'invoice_ids'
  | 'credit_note_ids'
  | 'accounting_payment_ids'
  | 'third_party_payment_ids'
  | 'workflow_id'
>;

export type newPropertiesObject = {
  addedInvoiceIds: Id[];
  newBalance: number;
};

type PaymentPlanEditFormStepsProps = {
  paymentPlan: PaymentPlanComplete;
  paymentSteps: PaymentStepsAttributes[];
  onSubmit: SubmitHandler<PartialUpdatePaymentPlanBody>;
};

export const PaymentPlanEditFormSteps = ({
  onSubmit,
  paymentPlan,
  paymentSteps,
}: PaymentPlanEditFormStepsProps) => {
  const history = useHistory();
  const { t } = useTranslation();

  const {
    attributes: { invoices, currency, total_amount },
  } = paymentPlan;

  const invoiceIds = [...(invoices?.details.map(({ id }) => id) || [])];
  const form = useForm<PartialUpdatePaymentPlanBody>({
    shouldUnregister: true,
    defaultValues: {
      workflow_id: paymentPlan.attributes.workflow.id,
      invoice_ids: invoiceIds,
      credit_note_ids: paymentPlan.attributes.credit_notes.map(({ id }) => String(id)),
      accounting_payment_ids: paymentPlan.attributes.accounting_payments.map(({ id }) =>
        String(id),
      ),
      third_party_payment_ids: paymentPlan.attributes.third_party_payments.map(({ id }) =>
        String(id),
      ),
      payment_steps_attributes: paymentSteps,
    },
  });

  const {
    control,
    formState: { errors, isDirty },
    watch,
    register,
    handleSubmit,
    setValue,
    reset,
    getValues,
  } = form;

  const { append, fields, remove } = useFieldArray<PartialUpdatePaymentPlanBody>({
    control,
    name: 'payment_steps_attributes',
  });

  useEffect(() => {
    reset({
      ...getValues,
      invoice_ids: invoiceIds,
      credit_note_ids: paymentPlan.attributes.credit_notes.map(({ id }) => String(id)),
      accounting_payment_ids: paymentPlan.attributes.accounting_payments.map(({ id }) =>
        String(id),
      ),
      third_party_payment_ids: paymentPlan.attributes.third_party_payments.map(({ id }) =>
        String(id),
      ),
      payment_steps_attributes: paymentSteps,
      workflow_id: paymentPlan.attributes.workflow.id,
    });
  }, [paymentSteps, paymentPlan.attributes.workflow.id, getValues, reset]);

  const steps = watch('payment_steps_attributes') || [];
  const dueDate = dayjs().toString();
  const stepsTotal = steps
    .filter(({ _destroy }) => !_destroy)
    .reduce((acc, { amount }) => acc + Number(amount), 0);

  const doAmountsMatch = Number(total_amount) === Math.round(stepsTotal * 100) / 100;

  const getMinDate = (index: number) => {
    const lastStep = steps[index - 1];

    if (isDefined(lastStep)) return dayjs(lastStep.due_date).add(1, 'day').toDate();
    return dayjs(dueDate).toDate();
  };

  register('invoice_ids');
  register('credit_note_ids');
  register('accounting_payment_ids');
  register('third_party_payment_ids');
  register('workflow_id');

  const removeStep = (index: any) => {
    if (steps[index]?.id) {
      setValue(`payment_steps_attributes.${index}._destroy`, true);
    } else {
      remove(index);
    }
  };

  const askRecalculation = (index: any) => {
    if (index === steps.length - 1) {
      return;
    }

    const { payment_steps_attributes: currentSteps, ...rest } = watch();

    modals.open({
      title: t(i18nKeys.CONFIRM),
      size: 'lg',
      centered: true,
      children: (
        <RecalculateDatesModal
          onConfirm={(newDelay: number) => {
            reset(
              {
                ...rest,
                payment_steps_attributes: adjustNextDatesSnakeCased(currentSteps, index, newDelay),
              },
              { keepTouched: true, keepDirty: true },
            );
          }}
        />
      ),
    });
  };

  const handleAdjustNextAmounts = (index: number) => {
    const { payment_steps_attributes: currentSteps, ...rest } = watch();

    reset(
      {
        ...rest,
        payment_steps_attributes: adjustNextAmounts(
          currentSteps
            .filter(step => !step._destroy)
            .map(step => ({ ...step, amount: Number(step.amount) })) as any,
          index,
          Number(total_amount || 0),
        ),
      },
      { keepTouched: true, keepDirty: true },
    );
  };

  const displayedFields = steps.filter(step => !step._destroy);

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <PaymentPlanFormWrapper>
          <PaymentPlanProperties paymentPlan={paymentPlan} />
          <Panel
            className={styles('steps-details')}
            title={t(i18nKeys.PAYMENT_PLAN.ATTRIBUTES.PAYMENT_STEPS)}
            topRight={
              <MButton
                variant="light"
                size="sm"
                leftSection={<IconPlus size={16} stroke={1.5} />}
                onClick={() =>
                  append({
                    can_edit: true,
                    amount: Math.round((Number(total_amount) - Number(stepsTotal)) * 100) / 100,
                    due_date: dayjs(steps[steps.length - 1].due_date)
                      .add(31, 'day')
                      .toISOString(),
                  })
                }
              >
                {t(i18nKeys.FORM.PAYMENT_PLAN.ADD_PAYMENT_STEP)}
              </MButton>
            }
          >
            <div className={styles('headers', 'edit')}>
              <div className={styles('header')}>{t(i18nKeys.DATE)}</div>
              <div className={styles('header')}>
                {t(i18nKeys.AMOUNT_CURRENCY, {
                  text: currencySymbol(currency),
                })}
              </div>
              <div className={styles('header')}>
                {t(i18nKeys.BALANCE)} ({currencySymbol(currency)})
              </div>
              <div className={styles('header')}> </div>
            </div>
            <div className={styles('steps')}>
              {fields.map((field, index) => {
                register(`payment_steps_attributes.${index}.id`);
                register(`payment_steps_attributes.${index}.due_date`);
                register(`payment_steps_attributes.${index}._destroy`);
                register(`payment_steps_attributes.${index}.can_edit`);
                register(`payment_steps_attributes.${index}.remaining_balance`);

                if (watch(`payment_steps_attributes.${index}._destroy`)) {
                  return undefined;
                }

                const isEditable = watch(`payment_steps_attributes.${index}.can_edit`);

                return (
                  <div key={field.id} className={styles('step', 'edit')}>
                    <DateSelector
                      required
                      name={`payment_steps_attributes.${index}.due_date`}
                      minDate={dayjs(getMinDate(index))}
                      disabled={!isEditable}
                      handleChange={(value: any) => {
                        setValue(`payment_steps_attributes.${index}.due_date`, value, {
                          shouldDirty: true,
                        });
                        askRecalculation(index);
                      }}
                    />
                    <Input
                      className={styles('amount-input')}
                      withBorder
                      disabled={!isEditable}
                      register={register(`payment_steps_attributes.${index}.amount`, {
                        required: true,
                        min: 0.01,
                      })}
                      errorMessage={get(errors, `payment_steps_attributes.${index}.amount`)}
                      noMargin
                      type="number"
                      step="0.01"
                    />
                    <Input
                      className={styles('amount-input', 'remaining-balance')}
                      disabled
                      value={watch(`payment_steps_attributes.${index}.remaining_balance`)}
                      noMargin
                      type="number"
                    />
                    <div className={styles('icons-wrapper')}>
                      {displayedFields.length > 1 && (
                        <>
                          {index !== fields.length - 1 && isEditable ? (
                            <Icon
                              title={t(
                                i18nKeys.FORM.PAYMENT_PLAN.STEP_CALCULATION.ADJUST_NEXT_AMOUNTS,
                              )}
                              name={IconName.MINIMAL_DOWN}
                              onClick={() => handleAdjustNextAmounts(index)}
                            />
                          ) : (
                            <span className={styles('empty-icon')} />
                          )}
                          {isEditable && (
                            <Icon name={IconName.TRASH_SIMPLE} onClick={() => removeStep(index)} />
                          )}
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={styles('steps-footer', 'edit')}>
              <div
                className={styles('total', doAmountsMatch ? ButtonColor.GREEN : ButtonColor.RED)}
              >
                <Amount value={stepsTotal} suffix={currency} />
                {doAmountsMatch ? (
                  ''
                ) : (
                  <Amount
                    value={Math.round((stepsTotal - Number(total_amount)) * 100) / 100}
                    suffix={currency}
                  />
                )}
              </div>
            </div>
            <Group justify="end">
              <MButton
                variant="light"
                color="orange"
                onClick={() => history.push(`payment-plans/${paymentPlan.id}`)}
              >
                {t(i18nKeys.CANCEL)}
              </MButton>
              <Button
                type="submit"
                label={t(i18nKeys.SAVE)}
                color={ButtonColor.MAIN}
                disabled={!doAmountsMatch || !isDirty}
                noMargin
              />
            </Group>
          </Panel>
        </PaymentPlanFormWrapper>
      </form>
    </FormProvider>
  );
};
